import {Form, notification, Space} from 'antd';
import {useMemo, useState} from 'react';
import {useAuth} from 'react-oidc-context';
import type {PrDispatchRequest} from '../../api/prdispatch';
import {BranchStep} from './BranchStep';
import {PrdispatchDetailsFields, type PrdispatchFormFields} from './PrdispatchDetailsFields';
import {ProjectStep} from './ProjectStep';
import {RepositoryStep} from './RepositoryStep';
import {handleSubmitError} from './submit-error-handler';
import {useProjectRepositories} from './useProjectRepositories';
import {useRepositoryBranches} from './useRepositoryBranches';
import {useSubmitPrDispatch} from './useSubmitPrDispatch';
import {useSupportedProjects} from './useSupportedProjects';

export const PrdispatchForm = () => {
    const [form] = Form.useForm<PrdispatchFormFields>();
    const [projectId, setProjectId] = useState<string | null>(null);
    const [repositoryId, setRepositoryId] = useState<string | null>(null);
    const [branchName, setBranchName] = useState<string | null>(null);
    const auth = useAuth();

    const projectsQuery = useSupportedProjects();
    const reposQuery = useProjectRepositories(projectId);
    const branchesQuery = useRepositoryBranches(projectId, repositoryId);
    const submit = useSubmitPrDispatch();

    const selectedRepo = useMemo(
        () => reposQuery.repositories.find((r) => r.id === repositoryId) ?? null,
        [reposQuery.repositories, repositoryId],
    );

    const submitting = submit.isPending;
    const canSubmit = Boolean(projectId && repositoryId && branchName && selectedRepo);

    const handleProjectSelect = (id: string) => {
        setProjectId(id);
        setRepositoryId(null);
        setBranchName(null);
    };

    const handleRepoSelect = (id: string) => {
        setRepositoryId(id);
        setBranchName(null);
    };

    const handleFinish = async (values: PrdispatchFormFields) => {
        if (submitting || !canSubmit || !selectedRepo || !branchName) {
            return;
        }
        if (!auth.isAuthenticated || !auth.user?.access_token) {
            notification.error({
                title: 'Sign in required',
                description: 'Your session has expired. Please sign in again to dispatch.',
            });
            await auth.signinRedirect();
            return;
        }

        const payload: PrDispatchRequest = {
            authorEmail: values.authorEmail,
            prompt: values.prompt,
            newFeatureBranchName: values.newFeatureBranchName,
            repoUrl: selectedRepo.webUrl,
            targetBranch: branchName,
        };

        try {
            await submit.mutateAsync(payload);
            notification.success({
                title: 'Dispatched',
                description: 'Your request has been queued.',
            });
            form.resetFields();
            setProjectId(null);
            setRepositoryId(null);
            setBranchName(null);
        } catch (err: unknown) {
            if (handleSubmitError(err) === 'unauthenticated') {
                await auth.signinRedirect();
            }
        }
    };

    return (
        <Space orientation='vertical' size='large' style={{width: '100%'}}>
            <ProjectStep
                projects={projectsQuery.projects}
                loading={projectsQuery.loading}
                error={projectsQuery.error}
                selectedProjectId={projectId}
                onSelect={handleProjectSelect}
                disabled={submitting}
            />
            <RepositoryStep
                projectSelected={projectId != null}
                repositories={reposQuery.repositories}
                loading={reposQuery.loading}
                error={reposQuery.error}
                selectedRepositoryId={repositoryId}
                onSelect={handleRepoSelect}
                disabled={submitting}
            />
            <BranchStep
                repositorySelected={repositoryId != null}
                branches={branchesQuery.branches}
                loading={branchesQuery.loading}
                error={branchesQuery.error}
                selectedBranch={branchName}
                onSelect={setBranchName}
                disabled={submitting}
            />
            <PrdispatchDetailsFields
                form={form}
                onFinish={handleFinish}
                submitting={submitting}
                canSubmit={canSubmit}
            />
        </Space>
    );
};
